import { useState, useRef, useCallback } from 'react';

interface AudioRecorderControls {
  isRecording: boolean;
  audioBlob: Blob | null;
  recordingError: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  resetAudio: () => void;
}

export function useAudioRecorder(): AudioRecorderControls {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recordingError, setRecordingError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const cleanup = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  }, []);

  const startRecording = useCallback(async () => {
    setAudioBlob(null);
    setRecordingError(null);
    cleanup();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        if (audioChunksRef.current.length > 0) {
          const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
          const blob = new Blob(audioChunksRef.current, { type: mimeType });
          setAudioBlob(blob);
        } else {
          setAudioBlob(null);
        }
        cleanup();
      };

      recorder.start();
      setIsRecording(true);
    } catch (err) {
      let errorMessage = "Error desconocido al iniciar grabación.";
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage = "Permiso para micrófono denegado.";
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage = "No se encontró un micrófono.";
        } else {
          errorMessage = `Error: ${err.message}`;
        }
      }
      setRecordingError(errorMessage);
      setIsRecording(false);
      cleanup();
    }
  }, [cleanup]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    } else {
      if (isRecording) setIsRecording(false);
    }
  }, [isRecording]);

  const resetAudio = useCallback(() => {
    setAudioBlob(null);
    setRecordingError(null);
  }, []);

  return { isRecording, audioBlob, recordingError, startRecording, stopRecording, resetAudio };
}